/**
 * Dialog „Zapisz” (filtry lub ustawienia): stan nazwy/nadpisywania,
 * mutacje POST/PUT do sales-crm/filters, zapis widoczności i kolejności kolumn.
 */
import { useState, useCallback } from 'react';
import { useDispatch } from 'store';
import { reduxActions } from 'store';
import {
  usePostSalesCrmFilters,
  usePutSalesCrmFilters
} from 'api';

export interface UseSaveFiltersDialogParams {
  buildFiltersFromRows: () => import('components/wise/types').IWiseTableAdvancedFilter[];
  pendingColumnVisibility: Record<string, boolean>;
  pendingColumnOrder: string[];
  selectedSettingsPresetIdFromRedux: string | null;
  componentSymbol: string;
  isDynamicUI: boolean;
  pageSymbol?: string;
  applyColumnVisibility: () => void;
}

export function useSaveFiltersDialog({
  buildFiltersFromRows,
  pendingColumnVisibility,
  pendingColumnOrder,
  selectedSettingsPresetIdFromRedux,
  componentSymbol,
  isDynamicUI,
  pageSymbol,
  applyColumnVisibility
}: UseSaveFiltersDialogParams) {
  const dispatch = useDispatch();
  const postFiltersMutation = usePostSalesCrmFilters(componentSymbol);
  const putFiltersMutation = usePutSalesCrmFilters(componentSymbol);

  const [saveNameDialogType, setSaveNameDialogType] = useState<'filters' | 'settings' | null>(null);
  const [saveNameValue, setSaveNameValue] = useState('');
  const [overwritePresetWhenSaving, setOverwritePresetWhenSaving] = useState(true);

  const handleCloseSaveNameDialog = useCallback(() => {
    setSaveNameDialogType(null);
    setSaveNameValue('');
  }, []);

  const handleOpenSaveFiltersNameDialog = useCallback(() => {
    setSaveNameDialogType('filters');
    setSaveNameValue('');
    setOverwritePresetWhenSaving(true);
  }, []);

  const handleOpenSaveSettingsNameDialog = useCallback(() => {
    applyColumnVisibility();
    setSaveNameDialogType('settings');
    setSaveNameValue('');
    setOverwritePresetWhenSaving(true);
  }, [applyColumnVisibility]);

  const handleConfirmSaveName = useCallback(() => {
    const filters = buildFiltersFromRows();
    const presetId = selectedSettingsPresetIdFromRedux ?? null;
    const overwrite = overwritePresetWhenSaving && presetId;
    const visibilityToSave = pendingColumnVisibility;
    const orderToSave = pendingColumnOrder.length > 0 ? pendingColumnOrder : undefined;

    const dispatchFilters = (f: typeof filters) => {
      if (isDynamicUI && pageSymbol) {
        dispatch(
          reduxActions.setWiseTableAdvancedFilters({
            pageSymbol,
            componentSymbol,
            filters: f
          })
        );
      } else {
        dispatch(
          reduxActions.setTraderPanelWiseTableAdvancedFilters({
            componentSymbol,
            filters: f
          })
        );
      }
    };

    if (saveNameDialogType === 'filters') {
      if (overwrite) {
        putFiltersMutation.mutate(
          {
            id: presetId,
            data: {
              component_symbol: componentSymbol,
              filters,
              column_visibility: visibilityToSave,
              ...(orderToSave && { column_order: orderToSave })
            }
          },
          { onSettled: () => handleCloseSaveNameDialog() }
        );
        dispatchFilters(filters);
      } else {
        const name = saveNameValue.trim();
        if (!name) return;
        dispatchFilters(filters);
        postFiltersMutation.mutate(
          {
            component_symbol: componentSymbol,
            name,
            filters,
            column_visibility: visibilityToSave,
            ...(orderToSave && { column_order: orderToSave })
          },
          { onSettled: () => handleCloseSaveNameDialog() }
        );
      }
    } else if (saveNameDialogType === 'settings') {
      if (overwrite) {
        putFiltersMutation.mutate(
          {
            id: presetId,
            data: {
              component_symbol: componentSymbol,
              filters,
              column_visibility: visibilityToSave,
              ...(orderToSave && { column_order: orderToSave })
            }
          },
          { onSettled: () => handleCloseSaveNameDialog() }
        );
      } else {
        const name = saveNameValue.trim();
        if (!name) return;
        postFiltersMutation.mutate(
          {
            component_symbol: componentSymbol,
            name,
            filters,
            column_visibility: visibilityToSave,
            ...(orderToSave && { column_order: orderToSave })
          },
          { onSettled: () => handleCloseSaveNameDialog() }
        );
      }
    }
  }, [
    saveNameDialogType,
    saveNameValue,
    buildFiltersFromRows,
    isDynamicUI,
    pageSymbol,
    componentSymbol,
    pendingColumnVisibility,
    pendingColumnOrder,
    dispatch,
    postFiltersMutation,
    putFiltersMutation,
    handleCloseSaveNameDialog,
    selectedSettingsPresetIdFromRedux,
    overwritePresetWhenSaving
  ]);

  return {
    saveNameDialogType,
    saveNameValue,
    setSaveNameValue,
    overwritePresetWhenSaving,
    setOverwritePresetWhenSaving,
    setSaveNameDialogType,
    handleOpenSaveFiltersNameDialog,
    handleOpenSaveSettingsNameDialog,
    handleCloseSaveNameDialog,
    handleConfirmSaveName
  };
}
